import React, { useEffect, useMemo, useState } from "react";
import { Pulse, X, Check, Lock, Lightning, NotePencil, Timer } from "@phosphor-icons/react";
import { toLocalDatetimeInputValue } from "@/lib/dateUtils";
import SymptomPicker from "@/components/SymptomPicker";
import VoiceNoteButton from "@/components/VoiceNoteButton";
import VoiceSmartFill from "@/components/VoiceSmartFill";
import {
  ChipSelector, FlareDetailSection, ConditionTrackerSection, TagsSection,
  DurationPicker, VaImpactSection, UnifiedTriggersSection, UnifiedTreatmentSection,
  SectionCard, AdvancedAccordion,
} from "@/components/SymptomFormSections";
import {
  getTrackerForIssue, splitMedNameStrength, BODY_SIDES, classifyConditionType,
} from "@/lib/symptomDiary";

const BODY_REGIONS = [
  "Head", "Neck", "Jaw",
  "L Shoulder", "R Shoulder", "L Arm", "R Arm", "L Elbow", "R Elbow", "L Hand", "R Hand",
  "Chest", "Abdomen",
  "Upper Back", "Mid Back", "Lower Back",
  "L Hip", "R Hip", "L Knee", "R Knee", "L Ankle", "R Ankle", "L Foot", "R Foot",
  "Skin", "Mental", "Sleep",
];

// ---- Draft auto-save (localStorage) ----
const DRAFT_KEY = "claimops-symptom-draft-v1";
function readDraft() {
  try { const raw = localStorage.getItem(DRAFT_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function writeDraft(form) {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch { /* quota */ }
}
function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
}
function draftHasContent(d) {
  return !!(d && (d.symptom || d.notes || d.copingUsed || (d.triggers || []).length || (d.tags || []).length));
}

export function emptyEntry() {
  return {
    date: toLocalDatetimeInputValue(),
    entryType: "daily",
    linkedIssueId: "",
    isUndiagnosed: false,
    suspectedCondition: "",
    symptom: "",
    symptomCode: "",
    bodySystem: "",
    severity: 5,
    mood: 5,
    duration: "",
    triggers: [],
    reliefMethods: [],
    bodyRegions: [],
    impact: [],
    copingUsed: "",
    notes: "",
    photos: [],
    endDate: "",
    bodySide: "",
    worstDay: false,
    repeatedUseImpact: "",
    missedActivities: [],
    missedDays: null,
    assistiveDevices: [],
    treatments: [],
    medsTaken: [],
    treatmentHelped: "",
    factors: {},
    conditionFields: {},
    tags: [],
  };
}

const MODES = [
  { id: "quick", label: "QUICK LOG", icon: Lightning, hint: "15-second check-in" },
  { id: "flare", label: "FLARE-UP", icon: Timer, hint: "Document an episode" },
  { id: "detailed", label: "DETAILED", icon: NotePencil, hint: "Full evidence entry" },
];

function SeveritySlider({ value, onChange, testId, label = "SEVERITY" }) {
  const color = value >= 7 ? "#C72E2E" : value >= 4 ? "#D78D1A" : value >= 1 ? "#1A8C52" : "#94A3B8";
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span className="field-label">{label}</span>
        <span style={{ fontWeight: 800, fontSize: 16, color, fontFamily: "JetBrains Mono, monospace" }}>{value}/10</span>
      </div>
      <input
        type="range" min="0" max="10" step="1" value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testId}
        style={{ width: "100%", accentColor: color }}
      />
    </div>
  );
}

function union(a = [], b = []) {
  return Array.from(new Set([...(a || []), ...(b || [])]));
}

/**
 * Symptom Tracker v4 entry form — restructured into 5 outcome-oriented sections:
 *   1. What happened?              (condition, severity, duration, body side)
 *   2. What caused or worsened it? (unified trigger chips + Advanced factors)
 *   3. What did you do for it?     (unified meds/treatment + Did it help?)
 *   4. What did it prevent?        (VA-impact chips)
 *   5. Notes
 *
 * Modes:
 *   quick    — sections 1 (compact), 3, 4, 5
 *   flare    — adds 2 + condition tracker + flare details
 *   detailed — all sections + Tags + Body Map (inside Advanced details)
 */
export default function SymptomEntryForm({ initial, initialMode, onCancel, onSave, issues, isFree, prescriptions }) {
  const [form, setForm] = useState(() => {
    if (initial) return { ...emptyEntry(), ...initial };
    const draft = readDraft();
    if (draftHasContent(draft)) return { ...emptyEntry(), ...draft };
    const base = emptyEntry();
    if (initialMode === "flare") base.entryType = "flare";
    if (initialMode === "episode") base.entryType = "episode";
    return base;
  });
  const [restoredDraft, setRestoredDraft] = useState(() => !initial && draftHasContent(readDraft()));
  const isEpisodeMode = initialMode === "episode";
  const [mode, setMode] = useState(() => {
    if (initial?.id) return "detailed";
    if (initialMode === "episode") return "detailed"; // episode reuses the detailed form
    if (initialMode) return initialMode;
    const draft = readDraft();
    if (draftHasContent(draft)) return draft.entryType === "flare" ? "flare" : "quick";
    return "quick";
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const sortedIssues = useMemo(
    () => [...issues].sort((a, b) => (a.claimedAs || "").localeCompare(b.claimedAs || "")),
    [issues],
  );

  // Auto-save drafts for NEW entries only (not edits / duplicates).
  const isNew = !initial;
  useEffect(() => {
    if (!isNew) return undefined;
    const t = setTimeout(() => { if (draftHasContent(form)) writeDraft(form); }, 700);
    return () => clearTimeout(t);
  }, [form, isNew]);

  const linkedIssue = useMemo(
    () => (form.linkedIssueId ? issues.find((i) => i.id === form.linkedIssueId) : null),
    [form.linkedIssueId, issues],
  );
  const tracker = useMemo(() => {
    if (form.isUndiagnosed) return getTrackerForIssue(form.suspectedCondition || form.symptom || "");
    return linkedIssue ? getTrackerForIssue(linkedIssue) : null;
  }, [form.isUndiagnosed, form.suspectedCondition, form.symptom, linkedIssue]);
  const conditionType = useMemo(() => {
    if (form.isUndiagnosed) return classifyConditionType(form.suspectedCondition || form.symptom || "");
    if (linkedIssue) return classifyConditionType(linkedIssue);
    return classifyConditionType(form.symptom || "");
  }, [form.isUndiagnosed, form.suspectedCondition, form.symptom, linkedIssue]);

  function patch(k, v) { setForm((p) => ({ ...p, [k]: v })); }
  function toggleArr(field, val) {
    const cur = form[field] || [];
    patch(field, cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val]);
  }

  function switchMode(m) {
    setMode(m);
    if (m === "flare") patch("entryType", "flare");
    else if (m === "quick") patch("entryType", "daily");
  }

  /** Merge LLM-parsed voice fields into the form — additive, never destructive. */
  function applyVoiceFields(f, transcript) {
    setForm((prev) => {
      const next = { ...prev };
      if (f.symptom && !prev.symptom) { next.symptom = f.symptom; next.symptomCode = ""; next.bodySystem = ""; }
      if (f.severity != null) next.severity = f.severity;
      if (f.duration && !prev.duration) next.duration = f.duration;
      if (f.bodySide && !prev.bodySide) next.bodySide = f.bodySide;
      next.triggers = union(prev.triggers, f.triggers);
      next.impact = union(prev.impact, f.impact);
      next.missedActivities = union(prev.missedActivities, f.missedActivities);
      next.worstDay = prev.worstDay || !!f.worstDay;
      const existingBases = new Set((prev.medsTaken || []).map((m) => splitMedNameStrength(m.name).base.toLowerCase()));
      const newMeds = (f.medications || [])
        .filter((m) => !existingBases.has(splitMedNameStrength(m.name).base.toLowerCase()))
        .map((m) => ({ prescriptionId: "", name: splitMedNameStrength(m.name).base + (splitMedNameStrength(m.name).strength ? ` ${splitMedNameStrength(m.name).strength}` : ""), helped: m.helped || "" }));
      next.medsTaken = [...(prev.medsTaken || []), ...newMeds];
      const note = f.notes || transcript || "";
      if (note) next.notes = (prev.notes ? `${prev.notes.trimEnd()} ` : "") + note;
      if (f.conditionHint && !prev.linkedIssueId && !prev.isUndiagnosed) {
        const hint = f.conditionHint.toLowerCase();
        const hit = issues.find((i) => (i.claimedAs || "").toLowerCase().includes(hint));
        if (hit) next.linkedIssueId = hit.id;
      }
      return next;
    });
  }

  async function submit() {
    if (!form.symptom.trim()) { setErr("Symptom description is required."); return; }
    if (!form.date) { setErr("Date is required."); return; }
    setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        date: form.date.length === 10 ? form.date : new Date(form.date).toISOString(),
        endDate: form.endDate
          ? (form.endDate.length === 10 ? form.endDate : new Date(form.endDate).toISOString())
          : "",
        missedDays: form.missedDays == null || form.missedDays === "" ? null : Number(form.missedDays),
        bodyRegions: isFree ? [] : form.bodyRegions,
        photos: isFree ? [] : form.photos,
      };
      await onSave(payload);
      clearDraft();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }

  function discardDraft() {
    clearDraft();
    setForm(emptyEntry());
    setRestoredDraft(false);
    setMode("quick");
  }

  // Section visibility by mode. Quick is intentionally the bare minimum.
  const show = {
    triggers: mode !== "quick",
    bodySide: mode !== "quick",
    tracker: mode !== "quick",
    flareSection: form.entryType === "flare" && mode !== "quick",
    advancedExtras: mode === "detailed",
    mood: mode === "detailed",
  };

  return (
    <div className="modal-backdrop" data-testid="symptom-form-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="modal-panel modal-panel--lg">
        <div className="modal-header">
          <Pulse size={16} weight="bold" />
          <span>{initial?.id ? "EDIT SYMPTOM ENTRY" : isEpisodeMode ? "START ONGOING EPISODE" : "NEW SYMPTOM ENTRY"}</span>
          <button className="modal-close" onClick={onCancel}><X size={14} weight="bold" /></button>
        </div>
        <div className="modal-body modal-body--scroll">
          {!initial?.id && !isEpisodeMode && (
            <div className="entry-mode-seg" data-testid="entry-mode-seg">
              {MODES.map((m) => {
                const Icon = m.icon;
                const active = mode === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    className={`entry-mode-btn ${active ? "entry-mode-btn--active" : ""}`}
                    onClick={() => switchMode(m.id)}
                    data-testid={`entry-mode-${m.id}`}
                  >
                    <Icon size={15} weight="bold" />
                    <span className="entry-mode-btn-label">{m.label}</span>
                    <span className="entry-mode-btn-hint">{m.hint}</span>
                  </button>
                );
              })}
            </div>
          )}
          {restoredDraft && (
            <div className="sym-draft-banner" data-testid="symptom-draft-banner">
              <span>Restored your unsaved draft — it auto-saves as you type.</span>
              <button
                type="button"
                className="btn btn--outline"
                style={{ padding: "3px 10px", fontSize: 10 }}
                onClick={discardDraft}
                data-testid="symptom-draft-discard"
              >
                START FRESH
              </button>
            </div>
          )}
          <VoiceSmartFill onApply={applyVoiceFields} />
          <div className="form-grid">
            {/* ============================= 1. WHAT HAPPENED? ============================= */}
            <SectionCard
              title="WHAT HAPPENED?"
              hint="Pick the symptom and how bad it was. The rest stays optional."
              testId="section-what-happened"
            >
              <label className="field field--span">
                <span className="field-label">SYMPTOM / SHORT DESCRIPTION *</span>
                <SymptomPicker
                  value={form.symptomCode ? { code: form.symptomCode, label: form.symptom, bodySystem: form.bodySystem, bodyRegion: "" } : (form.symptom ? { code: "", label: form.symptom, bodySystem: "", bodyRegion: "" } : null)}
                  onChange={(next) => {
                    setForm((prev) => ({
                      ...prev,
                      symptom: next.label,
                      symptomCode: next.code,
                      bodySystem: next.bodySystem,
                      bodyRegions: prev.bodyRegions && prev.bodyRegions.length > 0
                        ? prev.bodyRegions
                        : (next.bodyRegion ? [next.bodyRegion] : prev.bodyRegions),
                    }));
                  }}
                  onFreeText={(label) => setForm((prev) => ({ ...prev, symptom: label, symptomCode: "", bodySystem: "" }))}
                />
              </label>

              <label className="field field--span">
                <span className="field-label">RELATED CONDITION</span>
                <select
                  className="field-input"
                  value={form.isUndiagnosed ? "__undx__" : form.linkedIssueId}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "__undx__") setForm((p) => ({ ...p, isUndiagnosed: true, linkedIssueId: "" }));
                    else setForm((p) => ({ ...p, isUndiagnosed: false, linkedIssueId: v }));
                  }}
                  data-testid="symptom-link-compact"
                >
                  <option value="">General / not linked</option>
                  {sortedIssues.map((i) => <option key={i.id} value={i.id}>{i.claimedAs || `DC ${i.code}`}</option>)}
                  <option value="__undx__">Undiagnosed / other</option>
                </select>
                {form.isUndiagnosed && (
                  <input
                    className="field-input"
                    style={{ marginTop: 8 }}
                    value={form.suspectedCondition}
                    onChange={(e) => patch("suspectedCondition", e.target.value)}
                    placeholder='Suspected condition - e.g., "Gout?", "Sciatica?"'
                    data-testid="symptom-suspected"
                  />
                )}
              </label>

              <div className="field field--span">
                <SeveritySlider value={form.severity} onChange={(v) => patch("severity", v)} testId="symptom-severity" />
              </div>

              <DurationPicker value={form.duration} onChange={(v) => patch("duration", v)} />

              {show.bodySide && (
                <label className="field">
                  <span className="field-label">BODY SIDE</span>
                  <select className="field-input" value={form.bodySide || ""} onChange={(e) => patch("bodySide", e.target.value)} data-testid="symptom-bodyside">
                    <option value="">—</option>
                    {BODY_SIDES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              )}

              {mode !== "quick" && (
                <label className="field">
                  <span className="field-label">{form.entryType === "flare" ? "FLARE START *" : "DATE & TIME *"}</span>
                  <input
                    type="datetime-local"
                    className="field-input"
                    value={form.date?.slice(0, 16) || ""}
                    onChange={(e) => patch("date", e.target.value)}
                    data-testid="symptom-date"
                  />
                </label>
              )}

              {show.mood && (
                <div className="field field--span">
                  <SeveritySlider value={form.mood ?? 5} onChange={(v) => patch("mood", v)} testId="symptom-mood" label="MOOD" />
                </div>
              )}
            </SectionCard>

            {/* Condition-specific tracker (Mental, MSK, Gout, Tinnitus) appears
                under section 1 once a condition is linked. */}
            {show.tracker && tracker && <ConditionTrackerSection form={form} patch={patch} tracker={tracker} />}

            {/* Flare-specific extras for episode logging. */}
            {show.flareSection && <FlareDetailSection form={form} patch={patch} toggleArr={toggleArr} />}

            {/* ============================= 2. WHAT CAUSED OR WORSENED IT? ============================= */}
            {show.triggers && (
              <UnifiedTriggersSection
                form={form}
                patch={patch}
                toggleArr={toggleArr}
                conditionType={conditionType}
              />
            )}

            {/* ============================= 3. WHAT DID YOU DO FOR IT? ============================= */}
            <UnifiedTreatmentSection
              form={form}
              patch={patch}
              toggleArr={toggleArr}
              prescriptions={prescriptions}
              suggestedMeds={tracker?.suggestedMeds || []}
              conditionType={conditionType}
            />

            {/* ============================= 4. WHAT DID IT PREVENT? ============================= */}
            <VaImpactSection form={form} toggleArr={toggleArr} />

            {/* ============================= 5. NOTES ============================= */}
            <SectionCard
              title="NOTES"
              hint="Add anything important that the chips did not capture."
              testId="section-notes"
            >
              <label className="field field--span">
                <span className="field-label" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <span>{form.entryType === "flare" ? "NOTES / WORST-DAY DETAILS" : "NOTES"}</span>
                  <VoiceNoteButton
                    onText={(t) => setForm((prev) => ({
                      ...prev,
                      notes: (prev.notes ? `${prev.notes.trimEnd()} ` : "") + t,
                    }))}
                  />
                </span>
                <textarea
                  className="field-input"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => patch("notes", e.target.value)}
                  placeholder="Anything else an examiner should know... (or tap VOICE NOTE and just talk)"
                  data-testid="symptom-notes"
                />
              </label>
            </SectionCard>

            {/* ============================= ADVANCED EXTRAS ============================= */}
            {show.advancedExtras && (
              <SectionCard
                title="ADVANCED DETAILS"
                hint="Optional extras — body map, tags, and the legacy free-text coping field."
                testId="section-advanced-extras"
              >
                <AdvancedAccordion label="Body map" testId="bodymap-advanced">
                  {isFree ? (
                    <div
                      className="field field--span"
                      style={{ padding: 14, background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 4, fontSize: 12, color: "#7C4A03" }}
                      data-testid="symptom-bodymap-locked"
                    >
                      <Lock size={10} weight="bold" /> Body-region tagging is a Pro feature. Upgrade to mark where the symptom is felt and surface that detail in your printable C&P log.
                    </div>
                  ) : (
                    <div className="field field--span">
                      <ChipSelector
                        options={BODY_REGIONS}
                        value={form.bodyRegions}
                        onToggle={(v) => toggleArr("bodyRegions", v)}
                        testId="symptom-bodyregions"
                      />
                    </div>
                  )}
                </AdvancedAccordion>
                <AdvancedAccordion label="Tags" testId="tags-advanced">
                  <div className="field field--span">
                    <TagsSection form={form} patch={patch} toggleArr={toggleArr} />
                  </div>
                </AdvancedAccordion>
                <AdvancedAccordion label="Free-text coping (legacy)" testId="coping-advanced">
                  <label className="field field--span">
                    <span className="field-label">MEDICATIONS / COPING USED (FREE TEXT)</span>
                    <input
                      className="field-input"
                      value={form.copingUsed}
                      onChange={(e) => patch("copingUsed", e.target.value)}
                      placeholder="e.g., 800mg ibuprofen, hot pack, rest 1hr"
                      data-testid="symptom-coping"
                    />
                  </label>
                </AdvancedAccordion>
              </SectionCard>
            )}
          </div>
          {err && <div className="form-report form-report--err" data-testid="symptom-error">{err}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn--outline" onClick={onCancel} disabled={busy}>CANCEL</button>
          <button className="btn btn--accent" onClick={submit} disabled={busy} data-testid="symptom-save">
            <Check size={14} weight="bold" /> {busy ? "Saving..." : isEpisodeMode ? "START EPISODE" : "SAVE ENTRY"}
          </button>
        </div>
      </div>
    </div>
  );
}
